import { CompensationData, HistoricalCompensationData } from '@modules/EvaluationModule/interfaces/CompensationData';
import { CompensationHistoryTableHeaders } from '../../../enums/CompensationHistoryTableHeaders';
import { useStyles } from '../CompensationHistory.styles';
import { CompensationTypeLabel } from '@modules/EvaluationModule/enums/CompensationTypeLabel';
import classNames from 'classnames';
import { TableHeaderData } from '@shared/interfaces/TableHeaderData';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Tooltip from '@mui/material/Tooltip';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { ReactNode } from 'react';
import { formatFinancialValue, isUnderReview, UNDER_REVIEW_LABEL } from '../../../helpers/currencyOptions';

type TableRowFuncType = (compensationHistory: HistoricalCompensationData, itemNumber: number) => ReactNode;

export const getTableRow = ({
  tableHeaders,
  compensationData,
  isAddNewCompensationComplete,
  setCompensationForModal,
  isDetailsVisible,
}: {
  tableHeaders: TableHeaderData[];
  compensationData: CompensationData;
  isAddNewCompensationComplete: boolean;
  setCompensationForModal: (compensation: HistoricalCompensationData) => void;
  isDetailsVisible: boolean;
}): TableRowFuncType => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const styles = useStyles();

  const toDollarFormat = (value: string) => (
    <>
      <span className={styles.historyCurrency}>$</span> {value}
    </>
  );
  const tableColumns = {
    [CompensationHistoryTableHeaders.ReviewData]: {
      component: ({ period }: HistoricalCompensationData) => <>{format(parseISO(period), 'MMM ‘yy')}</>,
    },
    [CompensationHistoryTableHeaders.Base]: {
      component: ({ currentCompensation, compensationType }: HistoricalCompensationData) => (
        <>
          {isUnderReview(currentCompensation) ? (
            <span className={styles.noBonus}>—</span>
          ) : (
            <>
              {toDollarFormat(formatFinancialValue(currentCompensation ?? 0))}
              <span className={styles.salaryType}>
                {' '}
                {CompensationTypeLabel[compensationType as keyof typeof CompensationTypeLabel]}
              </span>
            </>
          )}
        </>
      ),
    },
    [CompensationHistoryTableHeaders.Bonus]: {
      component: ({ bonus, currentCompensation }: HistoricalCompensationData) => (
        <>
          {isUnderReview(currentCompensation) ? (
            <span className={styles.noBonus}>—</span>
          ) : bonus ? (
            toDollarFormat(formatFinancialValue(bonus))
          ) : (
            <span className={styles.noBonus}>–</span>
          )}
        </>
      ),
    },
    [CompensationHistoryTableHeaders.Total]: {
      component: ({ compensationRateMonthly, bonus, currentCompensation }: HistoricalCompensationData) => {
        if (isUnderReview(currentCompensation)) {
          return <span className={styles.salaryType}>{UNDER_REVIEW_LABEL}</span>;
        }
        const total = formatFinancialValue(Math.round(compensationRateMonthly + Number(bonus ?? 0)));
        return <>{toDollarFormat(total ?? 0)}</>;
      },
    },
    [CompensationHistoryTableHeaders.DifferenceCurrentTotalWithPrevious]: {
      component: (
        {
          compensationRateMonthly,
          bonus,
          isProcessing,
          compensationType,
          currentCompensation,
        }: HistoricalCompensationData,
        i: number
      ) => {
        if (isUnderReview(currentCompensation)) return null;

        const historicalCompensation =
          compensationData?.historicalCompensations[
            i < compensationData?.historicalCompensations.length - 1 ? i + 1 : i
          ];

        if (compensationType !== historicalCompensation?.compensationType) return null;
        const total = compensationRateMonthly + Number(bonus ?? 0);

        const previousTotal =
          historicalCompensation?.compensationRateMonthly + Number(historicalCompensation?.bonus ?? 0);
        const difference = Math.round((total / previousTotal - 1) * 100);
        return (
          <div className={styles.difference}>
            {!!difference && (
              <span>
                {difference > 0 && '+'}
                {difference}%{' '}
              </span>
            )}
            {isProcessing && (
              <Tooltip title={'Raise is being under Finance team review'} placement="top">
                <AccessTimeIcon className={styles.pendingConfirmationIcon}></AccessTimeIcon>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  };

  const compensationHistoryRow: TableRowFuncType = (compensationHistory, itemNumber) => {
    return (
      <tr
        className={classNames(styles.tableDataRow, {
          [styles.newHistoryItem]: isAddNewCompensationComplete && itemNumber === 0,
          [styles.tableDataRowHoverable]: isDetailsVisible,
        })}
        key={compensationHistory.period}
        onClick={isDetailsVisible ? () => setCompensationForModal(compensationHistory) : undefined}
      >
        {tableHeaders.map(({ title }) => (
          <td key={title}>
            {tableColumns[title as CompensationHistoryTableHeaders].component(compensationHistory, itemNumber)}
          </td>
        ))}
      </tr>
    );
  };
  return compensationHistoryRow;
};
